import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import { siteMeta, categories } from "@/mocks/articles";

const milestones = [
  { num: "01", title: "看见起落", text: "不神化、不妖魔化，把公众人物的高低起伏还原为可以被理解的叙事。" },
  { num: "02", title: "尊重边界", text: "涉私与未证实信息一律谨慎，反对借流量对个人进行无底线消费。" },
  { num: "03", title: "长期主义", text: "拒绝标题党与一锤子买卖，做经得起反复查看的长期内容。" },
];

export default function About() {
  usePageMeta({
    title: "关于我们_每日黑料人物盘点站的定位与内容原则",
    description:
      "关于每日黑料：一个面向大众的公众人物动态与热议话题盘点站。这里讲清楚我们的定位、发展理念、内容原则与口碑，也欢迎你通过读者联络提供建议与核实线索。",
    keywords: "每日黑料,关于我们,每日黑料人物,内容原则,公众人物盘点,读者联络",
    canonicalPath: "/about",
  });

  useJsonLd([
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: `关于我们 - ${siteMeta.brand}`,
      description: siteMeta.desc,
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: siteMeta.brand },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  ]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
      {/* 面包屑 */}
      <nav className="flex items-center gap-1.5 text-xs text-foreground-600" aria-label="面包屑">
        <Link to="/" className="flex items-center gap-1 hover:text-primary-700">
          <i className="ri-home-5-line"></i>首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-primary-700">关于我们</span>
      </nav>

      {/* 页首 */}
      <section className="mt-8 border-y border-foreground-900/10 bg-background-100/50 py-10 md:py-12">
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2">
          <div>
            <span className="inline-block whitespace-nowrap rounded-sm bg-primary-500 px-2 py-0.5 font-label text-xs tracking-widest text-background-50">
              ABOUT US
            </span>
            <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              我们把「看热闹」
              <br />
              做成了一门<span className="text-primary-500">认真的事</span>
            </h1>
            <p className="mt-4 text-[15px] leading-8 text-foreground-700">{siteMeta.desc}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { n: "4+", t: "常设盘点栏目" },
              { n: "6年", t: "编辑部积累" },
              { n: "1条", t: "红线：不造谣" },
            ].map((s) => (
              <div key={s.t} className="rounded-lg border border-foreground-900/10 bg-background-50 py-6">
                <p className="font-label text-3xl text-primary-500 md:text-4xl">{s.n}</p>
                <p className="mt-1 text-xs text-foreground-600">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="grid grid-cols-1 gap-10 py-10 md:grid-cols-2 md:py-14" aria-label="定位与理念">
        <div>
          <SectionHeading tag="POSITION" title="我们是谁" />
          <p className="mt-4 text-[15px] leading-8 text-foreground-700">
            {siteMeta.brand}是一个面向大众的公众人物动态与热议话题盘点站。我们不制造流量奇观，而是把散落在社交平台、
            采访与行业动态里的线索，整理成有脉络、有立场的盘点内容，帮助你更完整地理解一个公众人物及其所处的行业。
          </p>
          <p className="mt-3 text-[15px] leading-8 text-foreground-700">
            从明星、网红，到商界人物与公共人物，四个栏目覆盖不同圈层。谁被翻旧账、谁又出新瓜，不只是茶余饭后的谈资，
            更是观察社会情绪与行业生态的一扇窗口。
          </p>
        </div>
        <div>
          <SectionHeading tag="PHILOSOPHY" title="发展理念" />
          <p className="mt-4 text-[15px] leading-8 text-foreground-700">
            我们相信「流量与尊重可以共存」。相比一时的点击，更在意一条内容在发布多年后依然能站得住脚。因此编辑部把
            事实核实、边界把握与长期主义，写进了日常的每个选题与排版里，宁可慢一点，也不要错一点。
          </p>
          <ul className="mt-5 space-y-4">
            {milestones.map((m) => (
              <li key={m.num} className="flex gap-4">
                <span className="font-label text-2xl text-accent-600">{m.num}</span>
                <div>
                  <h3 className="text-base font-bold text-foreground-950">{m.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-foreground-600">{m.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="rounded-lg bg-foreground-950 px-6 py-10 text-background-100 md:px-10 md:py-12" aria-label="内容原则">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="font-label text-xs tracking-[0.3em] text-accent-400">PRINCIPLES</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-background-50 md:text-3xl">
              三条写进日子的内容红线
            </h2>
          </div>
          <Link to="/faq" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 hover:bg-accent-600">
            内容 FAQ<i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { icon: "ri-focus-2-line", t: "先核实，再盘点", d: "每条涉及事实的信息都标注来源与状态；未证实的说法会明确提示「待核实」，绝不把传闻包装成结论。" },
            { icon: "ri-scales-3-line", t: "就事论事，不做诛心", d: "讨论聚焦行为、作品与规则本身，不使用人格标签，不因一次争议否定一个人长期的专业积累。" },
            { icon: "ri-user-smile-line", t: "内容有温度，边界有底线", d: "标题不夸大、封面不猎奇，涉及隐私与未成年人信息一律回避，反对一切形式的网络暴力。" },
          ].map((p) => (
            <div key={p.t} className="rounded-lg border border-background-200/15 bg-background-100/5 p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className={`${p.icon} text-xl`}></i>
              </div>
              <h3 className="mt-4 text-lg font-bold text-background-50">{p.t}</h3>
              <p className="mt-2 text-sm leading-7 text-background-300">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 栏目一览 */}
      <section className="py-10 md:py-14" aria-label="栏目一览">
        <SectionHeading tag="COLUMNS" title="四个栏目，四扇观察窗口" />
        <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="group overflow-hidden rounded-lg border border-foreground-900/10 bg-background-100/60 p-5 transition-colors hover:border-primary-300"
            >
              <p className="font-label text-sm tracking-[0.2em] text-primary-600">{c.en}</p>
              <h3 className="mt-2 text-lg font-bold text-foreground-950 group-hover:text-primary-700">
                {c.name}
              </h3>
              <p className="mt-2 text-sm leading-6 text-foreground-600">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 口碑与联系 */}
      <section className="grid grid-cols-1 gap-8 border-t border-foreground-900/10 py-10 lg:grid-cols-2 md:py-14" aria-label="口碑与联系">
        <div>
          <SectionHeading tag="VOICE" title="读者怎么说" />
          <ul className="mt-6 space-y-4">
            {[
              { q: "「把每个瓜都讲清楚来龙去脉，不煽动、不站队，难得。」", w: "读者 · 林先生" },
              { q: "「有些栏目盘点是真能做功课的，看完整个人都清醒了。」", w: "读者 · 陈小姐" },
              { q: "「会主动标注'待核实'，这种克制在热榜里很少见。」", w: "读者 · 自媒体同行" },
            ].map((v) => (
              <li key={v.w} className="rounded-lg border border-foreground-900/10 bg-background-100/50 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <p className="mt-1 text-[15px] leading-7 text-foreground-800">{v.q}</p>
                <p className="mt-2 text-xs text-foreground-500">{v.w}</p>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <SectionHeading tag="CONTACT" title="保持联系" />
          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
              <i className="ri-mail-send-line text-xl text-primary-600"></i>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">编辑部邮箱</h3>
                <a href={`mailto:${siteMeta.email}`} className="mt-1 block text-sm text-primary-700 hover:underline">
                  {siteMeta.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
              <i className="ri-feedback-line text-xl text-accent-600"></i>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">线索与纠错</h3>
                <p className="mt-1 text-sm leading-6 text-foreground-600">
                  如果你掌握可核实的线索，或发现某篇盘点的信息需要更正，欢迎提交。核实通过后我们会在第一时间更新。
                </p>
                <Link to="/contact" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline">
                  前往读者联络页<i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}